/**
 * Verschiedene häufig genutzte Auflistungen.
 *
 * @author Andreas Wenger <andi@xenoage.com>
 */

/**
 * Anrede.
 * Nummerierung wie in Ruhe Sanft, wobei 3 = "Firma"
 * und nicht "das Kind" (nicht genutzt in dieser App).
 */
export enum Anrede {
  Frau = 1,
  Herr = 2,
  Firma = 3,
  OhneAnrede = 4
}

/**
 * Bestattungsart.
 * Nummerierung anders als in Ruhe Sanft, da Chars als
 * enum-Werte nicht möglich sind.
 */
export enum BestattungsArt {
  Erd = 1,
  Feuer = 2
}

/**
 * Wahr/Ja, Falsch/Nein oder keine Angabe.
 */
export enum BooleanOpt {
  KeineAngabe = 0,
  Wahr = 1,
  Falsch = 2
}

/**
 * Familienstand.
 * Nummerierung wie in Ruhe Sanft.
 */
export enum Familienstand {
  Unbekannt = 0,
  Verheiratet = 1,
  Ledig = 2,
  Verwitwet = 3,
  Geschieden = 4,
  FuerTotErklaert = 5,
  Aufgehoben = 6,
  FuerNichtigErklaert = 7,
  Lebenspartnerschaft = 8
}

/**
 * Status eines Sterbefall-Eintrags.
 */
export enum SterbefallStatus {
  Entwurf = 1,
  Gesendet = 2,
  Geloescht = 3
}

/**
 * Todesursache.
 * Nummerierung wie in Ruhe Sanft.
 */
export enum Todesursache {
  Natuerlich = 1,
  Unnatuerlich = 2,
  Ungeklaert = 3
}

/**
 * Art der Verfügung:
 * Testament, Erbvertrag oder Ehe- und Erbvertrag,
 * oder keine der drei Möglichkeiten; oder unbekannt.
 */
export enum VerfuegungArt {
  Unbekannt = 0,
  NichtVorhanden = 1,
  Testament = 2,
  Erbvertrag = 3,
  EheUndErbvertrag = 4
}

/**
 * Art der Verwandtschaft.
 * Nummerierung bis Index 7 wie in Ruhe Sanft.
 */
export enum VerwandtschaftsArt {
  SonstigerErbe = 0,
  KindVolljaehrig = 1,
  KindMinderjaehrig = 2,
  KindVerstorben = 3,
  KindVonVerstKind = 4,
  KindVonVerstGeschwister = 5,
  Geschwister = 6,
  Elternteil = 7,
  //weitere Verwandschaftsverhältnisse (nicht in Ruhe Sanft definiert)
  Vater = 8, //in Ruhe Sanft ggf. zu 7 konvertieren
  Mutter = 9, //in Ruhe Sanft ggf. zu 7 konvertieren
  Ehepartner = 10
};

/**
 * Vormunds-Verhältnis.
 * Nummerierung wie in Ruhe Sanft.
 */
export enum Vormund {
  Vormund = 1,
  Pfleger = 2,
  Gegenvormund = 3,
  Beistand = 4,
  Betreuer = 5
}

/**
 * Bezeichnungen der Auflistungswerte.
 */
export class EnumNames {

  static get anredeNames() { return {
    1: "Frau",
    2: "Herr",
    3: "Firma",
    4: "(ohne Anrede)"
  }};

  static get anredeNamesOhneFirma() { return {
    1: "Frau",
    2: "Herr",
    4: "(ohne Anrede)"
  }};

  static getAnredeName(anrede: Anrede) {
    return this.anredeNames[anrede];
  }

  static get bestattungsArtNames() { return {
    1: "Erd-Bestattung",
    2: "Feuer-Bestattung",
  }};

  static getBestattungsArtName(v: BestattungsArt) {
    return this.bestattungsArtNames[v];
  }

  static get booleanOptNames() { return {
    0: "keine Angabe",
    1: "ja",
    2: "nein"
  }};

  static getBooleanOptName(v: BooleanOpt) {
    return this.booleanOptNames[v];
  }

  static get booleanOptNamesVorhanden() { return {
    0: "keine Angabe",
    1: "vorhanden",
    2: "nicht vorhanden"
  }};

  static getBooleanOptNamesVorhanden(v: BooleanOpt) {
    return this.booleanOptNamesVorhanden[v];
  }

  static get familienstandNames() { return {
    0: "unbekannt",
    1: "verheiratet",
    2: "ledig",
    3: "verwitwet",
    4: "geschieden",
    5: "für tot erklärt",
    6: "aufgehoben",
    7: "für nichtig erklärt",
    8: "Lebenspartnerschaft"
  }};

  static getFamilienstandName(familienstand: Familienstand) {
    return this.familienstandNames[familienstand];
  }

  static get sterbefallStatusNames() { return {
    1 : "Entwurf",
    2 : "Gesendet",
    3 : "Gelöscht"
  }};

  static getSterbefallStatusName(sterbefallStatus: SterbefallStatus) {
    return this.sterbefallStatusNames[sterbefallStatus];
  }

  static get todesursacheNames() { return {
    1 : "natürlich",
    2 : "unnatürlich",
    3 : "ungeklärt"
  }};

  static getTodesursacheName(todesursache: Todesursache) {
    return this.todesursacheNames[todesursache];
  }

  static get verfuegungArtNames() { return {
    0: "unbekannt",
    1: "nicht vorhanden",
    2: "Testament",
    3: "Erbvertrag",
    4: "Ehe- und Erbvertrag"
  }};

  static getVerfuegungArt(verfuegungArt: VerfuegungArt) {
    return this.verfuegungArtNames[verfuegungArt];
  }

	static get verwandtschaftsArtNames() { return {
		0: "Sonstiger Erbe",
		1: "Kind, volljährig",
		2: "Kind, minderjährig",
		3: "Kind, verstorben",
		4: "Kind eines verst. Kinds",
		5: "Kind eines verst. Geschwisters",
		6: "Geschwister",
		7: "Elternteil",
    8: "Vater",
    9: "Mutter",
    10: "Ehepartner"
	}};

  static getVerwandtschaftsArtName(art: VerwandtschaftsArt) {
    return this.verwandtschaftsArtNames[art];
  }

  static get vormundNames() { return {
    "" : "",
    1: "Vormund",
    2: "Pfleger",
    3: "Gegenvormund",
    4: "Beistand",
    5: "Betreuer",
  }};

  static getVormundName(vormund: Vormund) {
    return this.vormundNames[vormund];
  }

}



// WEBPACK FOOTER //
// ./src/model/enums.ts